import React, { useEffect, useState } from 'react';
import apiService from '../../../services/api';
import styles from '../../../styles/AppraisalReport.module.css';

interface AppraisalReportProps {
    userId: string;
    academicYear?: string;
    onClose?: () => void;
}

interface ReportData {
    user_info: {
        name: string;
        designation: string;
        department: string;
        joiningDate?: string; // These might be missing from backend, handling gracefully
        qualifications?: string;
    };
    academic_years: string[];
    part_a: {
        academic_performance: any[];
        student_feedback: any[];
        reading_material: any[];
        innovative_teaching: any[];
        exam_duties: any[];
    };
    part_b: {
        extension_activity: any[];
        corporate_life: any[];
        professional_development: any[];
    };
    part_c: {
        research_papers: any[];
        research_projects: any[];
    };
    summary_scores: {
        part_a: number;
        part_b: number;
        part_c: number;
        total: number;
    };
}

const AppraisalReport: React.FC<AppraisalReportProps> = ({ userId, academicYear, onClose }) => {
    const [reportData, setReportData] = useState<ReportData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedYear, setSelectedYear] = useState<string>(academicYear || '');

    const renderApprovalCell = (approvals: any[]) => {
        if (!approvals || approvals.length === 0) return '-';
        return (
            <div style={{ fontSize: '0.85em' }}>
                {approvals.map((app: any, idx: number) => (
                    <div key={idx} style={{ marginBottom: '8px', borderBottom: idx < approvals.length - 1 ? '1px dashed #ccc' : 'none', paddingBottom: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <div><strong>{app.reviewer}</strong> <span style={{ color: '#555', fontSize: '0.9em' }}>({app.role})</span></div>
                            <div style={{ fontSize: '0.9em', color: '#333' }}>{app.date}</div>
                            <div><strong>Grade: {app.grade}</strong> ({app.points} pts)</div>
                        </div>
                        <div style={{
                            marginLeft: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            border: '1.5px solid green',
                            color: 'green',
                            fontSize: '12px',
                            flexShrink: 0
                        }}>
                            &#10003;
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    useEffect(() => {
        fetchReport(selectedYear);
    }, [userId, selectedYear]);

    const fetchReport = async (year: string) => {
        try {
            setLoading(true);
            const response = await apiService.getFacultyAppraisalReport(userId, year);
            if (response && response.status === 'success') {
                setReportData(response.data);
            } else {
                setError(response?.message || 'Failed to load report');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Error fetching report');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = async () => {
        try {
            // Create a temporary link to trigger download via API url directly
            // Or use blob fetch if authenticated headers are needed (which they are).
            // Since apiService handles auth, we should add a method there or use a direct fetch with token.
            // Let's use apiService to get blob.
            const response = await apiService.downloadAppraisalPdf(userId, selectedYear);

            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Appraisal_Report_${reportData?.user_info.name}_${selectedYear || 'Current'}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
        } catch (err: any) {
            console.error("Download failed", err);
            const errorMessage = err.response?.data?.message || err.response?.data?.error || "Failed to download PDF";
            alert(errorMessage);
        }
    };

    if (loading) return <div className={styles.loading}>Loading Report...</div>;
    if (error) return <div className={styles.error}>Error: {error}</div>;
    if (!reportData) return <div className={styles.noData}>No data available</div>;

    return (
        <div>
            <div className={`${styles.controls} ${styles.noPrint}`}>
                <button onClick={onClose} className={styles.cancelButton}>Close</button>
                <button onClick={handleDownloadPdf} className={styles.printButton}>Download PDF</button>
            </div>

            <div className={styles.container}>
                {/* Header Section */}
                <div className={styles.header}>
                    <img src="/images/image.png" alt="Logo" className={styles.logo} />
                    <div className={styles.institutionDetails}>
                        <div className={styles.institutionName}>LENDI INSTITUTE OF ENGINEERING AND TECHNOLOGY</div>
                        <div className={styles.institutionSub}>An Autonomous Institution</div>
                        <div className={styles.institutionSub}>Approved by AICTE & Permanently Affiliated to JNTUK, Kakinada</div>
                        <div className={styles.institutionSub}>Accredited by NAAC with "A" Grade</div>
                        <div className={styles.institutionSub}>Jonnada (Village), Denkada (Mandal), Vizianagaram Dist – 535 005</div>
                        <div className={styles.institutionSub}>Phone No. 08922-241111, 241112</div>
                        <div className={styles.institutionSub}>E-Mail: lendi_2008@yahoo.com Website: www.lendi.org</div>
                    </div>
                </div>

                <div className={styles.reportTitle}>Self-Appraisal Report of Faculty</div>

                {/* Faculty Details */}
                <h4 className={styles.subTitle}>Faculty Details</h4>
                <table className={`${styles.table} ${styles.facultyDetailsTable}`}>
                    <tbody>
                        <tr>
                            <td>Name of the Faculty</td>
                            <td>{reportData.user_info.name}</td>
                        </tr>
                        <tr>
                            <td>Present Designation</td>
                            <td>{reportData.user_info.designation}</td>
                        </tr>
                        <tr>
                            <td>Department</td>
                            <td>{reportData.user_info.department}</td>
                        </tr>
                        <tr>
                            <td>Academic Year</td>
                            <td>{selectedYear}</td>
                        </tr>
                    </tbody>
                </table>

                {/* Part A */}
                <div className={styles.sectionTitle}>PART-A TEACHING, LEARNING AND EVALUATION RELATED ACTIVITIES</div>

                <h4 className={styles.subTitle}>1) Academic Performance</h4>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Name of the course Handled</th>
                            <th>% of Lecture Completed</th>
                            <th>Year & Sem</th>
                            <th>No. of students</th>
                            <th>Distinctions Percentage</th>
                            <th>First classes Percentage</th>
                            <th>Pass Percentage</th>
                            <th>Score</th>
                            <th>Approvals</th>
                            <th>Final Awarded</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.part_a.academic_performance.length > 0 ? (
                            reportData.part_a.academic_performance.map((item, index) => {
                                const students = item.noStudents || 1;
                                const distPct = ((item.noDistinctions || 0) / students) * 100;
                                const firstPct = ((item.noFirstClasses || 0) / students) * 100;
                                const passPct = ((item.noPassed || 0) / students) * 100;
                                const score = (passPct / 100 * 30) + (firstPct / 100 * 5) + (distPct / 100 * 15);

                                return (
                                    <tr key={index}>
                                        <td className={styles.centerText}>{index + 1}</td>
                                        <td>{item.courseName}</td>
                                        <td className={styles.centerText}>{item.lectureCompleted || '-'}</td>
                                        <td className={styles.centerText}>{item.yearSem}</td>
                                        <td className={styles.centerText}>{item.noStudents}</td>
                                        <td className={styles.centerText}>{distPct.toFixed(0)}%</td>
                                        <td className={styles.centerText}>{firstPct.toFixed(0)}%</td>
                                        <td className={styles.centerText}>{passPct.toFixed(0)}%</td>
                                        <td className={styles.centerText}>{score.toFixed(2)}</td>
                                        <td>
                                            {renderApprovalCell(item.approvals)}
                                        </td>
                                        <td className={styles.centerText}>
                                            {item.task_final_score !== undefined ? item.task_final_score : '-'}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr><td colSpan={11} className={styles.centerText}>No records found</td></tr>
                        )}
                    </tbody>
                </table>

                <h4 className={styles.subTitle}>2) Feedback</h4>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Semester</th>
                            <th>Name of the Course Handled</th>
                            <th>Feedback %</th>
                            <th>Approvals</th>
                            <th>Final Awarded</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.part_a.student_feedback.length > 0 ? (
                            reportData.part_a.student_feedback.map((item, index) => (
                                <tr key={index}>
                                    <td className={styles.centerText}>{index + 1}</td>
                                    <td className={styles.centerText}>{item.yearSem}</td>
                                    <td>{item.courseName}</td>
                                    <td className={styles.centerText}>{item.feedbackPercentage}%</td>
                                    <td>
                                        {renderApprovalCell(item.approvals)}
                                    </td>
                                    <td className={styles.centerText}>
                                        {item.task_final_score !== undefined ? item.task_final_score : '-'}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan={6} className={styles.centerText}>No records found</td></tr>
                        )}
                    </tbody>
                </table>

                <h4 className={styles.subTitle}>3) Other Teaching-Learning Activities</h4>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Activity Description</th>
                            <th>Rating/Details</th>
                            <th>Approvals</th>
                            <th>Final Awarded</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.part_a.innovative_teaching.map((activity, idx) => (
                            <tr key={idx}>
                                <td className={styles.centerText}>{idx + 1}</td>
                                <td>{activity.activityDescription || activity.description}</td>
                                <td>{activity.qualityRating || '-'}</td>
                                <td>
                                    {renderApprovalCell(activity.approvals)}
                                </td>
                                <td className={styles.centerText}>
                                    {activity.task_final_score !== undefined ? activity.task_final_score : '-'}
                                </td>
                            </tr>
                        ))}
                        {reportData.part_a.innovative_teaching.length === 0 && (
                            <tr><td colSpan={5} className={styles.centerText}>No other activities recorded</td></tr>
                        )}
                    </tbody>
                </table>

                <div className={styles.finalSummary}>
                    <div className={styles.summaryRow}>
                        <strong>Part A Total Score:</strong>
                        <strong>{reportData.summary_scores.part_a}</strong>
                    </div>
                </div>

                {/* Part B */}
                <div className={styles.sectionTitle}>PART-B CO-CURRICULAR, EXTENSION, PROFESSIONAL DEVELOPMENT RELATED ACTIVITIES</div>

                <h4 className={styles.subTitle}>(i) Extension, Co-curricular & Field based activities</h4>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Activity</th>
                            <th>Role / Duration</th>
                            <th>Approvals</th>
                            <th>Final Awarded</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.part_b.extension_activity.map((item, idx) => (
                            <tr key={idx}>
                                <td className={styles.centerText}>{idx + 1}</td>
                                <td>{item.description || item.activity}</td>
                                <td>{item.role}</td>
                                <td>
                                    {renderApprovalCell(item.approvals)}
                                </td>
                                <td className={styles.centerText}>
                                    {item.task_final_score !== undefined ? item.task_final_score : '-'}
                                </td>
                            </tr>
                        ))}
                        {reportData.part_b.extension_activity.length === 0 && (
                            <tr><td colSpan={5} className={styles.centerText}>No records found</td></tr>
                        )}
                    </tbody>
                </table>

                <h4 className={styles.subTitle}>(ii) Contribution to Corporate Life and Management of the Institution</h4>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Activity / Role</th>
                            <th>Details</th>
                            <th>Approvals</th>
                            <th>Final Awarded</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.part_b.corporate_life.map((item, idx) => (
                            <tr key={idx}>
                                <td className={styles.centerText}>{idx + 1}</td>
                                <td>{item.description || item.activity}</td>
                                <td>{item.role}</td>
                                <td>
                                    {renderApprovalCell(item.approvals)}
                                </td>
                                <td className={styles.centerText}>
                                    {item.task_final_score !== undefined ? item.task_final_score : '-'}
                                </td>
                            </tr>
                        ))}
                        {reportData.part_b.corporate_life.length === 0 && (
                            <tr><td colSpan={5} className={styles.centerText}>No records found</td></tr>
                        )}
                    </tbody>
                </table>

                <h4 className={styles.subTitle}>(iii) Professional Development Activities</h4>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Activity / Membership</th>
                            <th>Role / Type</th>
                            <th>Approvals</th>
                            <th>Final Awarded</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.part_b.professional_development.map((item, idx) => (
                            <tr key={idx}>
                                <td className={styles.centerText}>{idx + 1}</td>
                                <td>{item.description || item.activity}</td>
                                <td>{item.role || item.type}</td>
                                <td>
                                    {renderApprovalCell(item.approvals)}
                                </td>
                                <td className={styles.centerText}>
                                    {item.task_final_score !== undefined ? item.task_final_score : '-'}
                                </td>
                            </tr>
                        ))}
                        {reportData.part_b.professional_development.length === 0 && (
                            <tr><td colSpan={5} className={styles.centerText}>No records found</td></tr>
                        )}
                    </tbody>
                </table>

                <div className={styles.finalSummary}>
                    <div className={styles.summaryRow}>
                        <strong>Part B Total Score:</strong>
                        <strong>{reportData.summary_scores.part_b}</strong>
                    </div>
                </div>


                {/* Part C */}
                <div className={styles.sectionTitle}>PART-C RESEARCH AND ACADEMIC CONTRIBUTIONS</div>

                <h4 className={styles.subTitle}>1) Research Publications</h4>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Title</th>
                            <th>Journal/Conf</th>
                            <th>ISSN/ISBN</th>
                            <th>Role</th>
                            <th>Approvals</th>
                            <th>Final Awarded</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.part_c.research_papers.length > 0 ? (
                            reportData.part_c.research_papers.map((item, index) => (
                                <tr key={index}>
                                    <td className={styles.centerText}>{index + 1}</td>
                                    <td>{item.title}</td>
                                    <td>{item.journalName}</td>
                                    <td>{item.issnIsbn}</td>
                                    <td>{item.isMainAuthor ? 'Main Author' : 'Co-Author'}</td>
                                    <td>
                                        {renderApprovalCell(item.approvals)}
                                    </td>
                                    <td className={styles.centerText}>
                                        {item.task_final_score !== undefined ? item.task_final_score : '-'}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan={7} className={styles.centerText}>No records found</td></tr>
                        )}
                    </tbody>
                </table>

                <h4 className={styles.subTitle}>2) Research Projects & Guidance</h4>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Category</th>
                            <th>Title/Topic</th>
                            <th>Status</th>
                            <th>Approvals</th>
                            <th>Final Awarded</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.part_c.research_projects.map((item, idx) => (
                            <tr key={idx}>
                                <td className={styles.centerText}>{idx + 1}</td>
                                <td>{item.category}</td>
                                <td>{item.title} - {item.details}</td>
                                <td>{item.status}</td>
                                <td>
                                    {renderApprovalCell(item.approvals)}
                                </td>
                                <td className={styles.centerText}>
                                    {item.task_final_score !== undefined ? item.task_final_score : '-'}
                                </td>
                            </tr>
                        ))}
                        {reportData.part_c.research_projects.length === 0 && (
                            <tr><td colSpan={6} className={styles.centerText}>No records found</td></tr>
                        )}
                    </tbody>
                </table>

                <div className={styles.finalSummary}>
                    <div className={styles.summaryRow}>
                        <strong>Part C Total Score:</strong>
                        <strong>{reportData.summary_scores.part_c}</strong>
                    </div>
                </div>

                {/* Overall Summary */}
                <div className={styles.finalSummary} style={{ marginTop: '40px', border: '2px solid black' }}>
                    <h3 style={{ textAlign: 'center', margin: '0 0 10px 0', borderBottom: '1px solid black', paddingBottom: '5px' }}>Final Summary</h3>
                    <div className={styles.summaryRow}>
                        <span>Part A (Teaching & Learning)</span>
                        <span>{reportData.summary_scores.part_a}</span>
                    </div>
                    <div className={styles.summaryRow}>
                        <span>Part B (Professional Development)</span>
                        <span>{reportData.summary_scores.part_b}</span>
                    </div>
                    <div className={styles.summaryRow}>
                        <span>Part C (Research)</span>
                        <span>{reportData.summary_scores.part_c}</span>
                    </div>
                    <div className={styles.summaryRow} style={{ fontSize: '16px', marginTop: '10px' }}>
                        <strong>Grand Total</strong>
                        <strong>{reportData.summary_scores.total}</strong>
                    </div>
                </div>

                <div style={{ marginTop: '50px', display: 'flex', justifyContent: 'space-between', padding: '0 20px' }}>
                    <div style={{ textAlign: 'center' }}>
                        <br /><br />
                        <strong>Signature of the Faculty</strong>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <br /><br />
                        <strong>Signature of the HOD</strong>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AppraisalReport;
